import { Search, LineChart, Smartphone, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { purpleColors, violetColors, slateColors, Section, Container, SectionTitle, SectionDescription } from '../styles';

const stepsMeta = [Search, LineChart, Smartphone, CheckCircle2] as const;

type HowItWorksProps = {};

const getLocalizedDelayDistributionScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Train%20stats/Distributionchart';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Delaydistribution_DE_cropped.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Delaydistribution_FR_cropped.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Delaydistribution_NL_cropped.jpeg`;
  }
  return `${base}/Delaydistribution_EN_cropped.jpeg`;
};

const getLocalizedTransferEvolutionScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Transfer%20stats';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Transferevolution_DE.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Transferevolution_FR.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Transferevolution_NL.jpeg`;
  }
  return `${base}/Transferevolution_EN.jpeg`;
};

const getLocalizedPlatformStatsScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Platformstats';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Platformstats_DE.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Platformstats_FR.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Platformstats_NL.jpeg`;
  }
  return `${base}/Platformstats_EN.jpeg`;
};

const HowItWorksSection = styled(Section)`
  background: white;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 5rem;
  position: relative;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StepCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StepConnector = styled.div`
  display: none;
  position: absolute;
  top: 3.5rem;
  left: 60%;
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, ${purpleColors[200]}, ${violetColors[500]}40);
  
  @media (min-width: 1024px) {
    display: block;
  }
`;

const IconContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const IconWrapper = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: 1rem;
  background: linear-gradient(to bottom right, ${purpleColors[500]}, ${violetColors[500]});
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  
  svg {
    width: 2.5rem;
    height: 2.5rem;
    color: white;
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  width: 2rem;
  height: 2rem;
  background: ${slateColors[900]};
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  font-weight: 700;
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin-bottom: 0.75rem;
`;

const StepDescription = styled.p`
  color: ${slateColors[600]};
  line-height: 1.6;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: center;
  margin-top: 6rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }
`;

const ContentColumn = styled.div<{ order?: number }>`
  order: 2;
  
  @media (min-width: 1024px) {
  order: ${props => props.order || 1};
  }
`;

const ImageColumn = styled.div<{ order?: number }>`
  order: 1;
  position: relative;
  
  @media (min-width: 1024px) {
    order: ${props => props.order || 1};
  }
  
  img {
    width: 90%;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }
`;

const SubHeading = styled.h3`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1.5rem;
`;

const SubDescription = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BulletDot = styled.div<{ color?: string }>`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: ${props => {
    if (props.color === 'violet') return `${violetColors[500]}20`;
    return `${purpleColors[100]}`;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &::after {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 9999px;
    background: ${props => {
      if (props.color === 'violet') return violetColors[600];
      return purpleColors[600];
    }};
  }
`;

const BulletText = styled.span`
  color: ${slateColors[700]};
`;

const DiveDeepSection = styled.div`
  margin-top: 6rem;
  background: linear-gradient(to bottom, ${purpleColors[50]}, ${violetColors[500]}15, ${purpleColors[50]});
  border-radius: 1.5rem;
  padding: 4rem 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }
`;

const DiveDeepTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  color: ${slateColors[900]};
  text-align: center;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const DiveDeepSubtitle = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  text-align: center;
  max-width: 48rem;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

export default function HowItWorks({}: HowItWorksProps) {
  const { t, i18n } = useTranslation();

  const stepKeys = ['search', 'find', 'track', 'travel'];

  return (
    <HowItWorksSection>
      <Container>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <SectionTitle>{t('appLandingPage.howItWorks.heading')}</SectionTitle>
          <SectionDescription>{t('appLandingPage.howItWorks.description')}</SectionDescription>
        </div>

        <StepsGrid>
          {stepKeys.map((stepKey, index) => {
            const Icon = stepsMeta[index];
            return (
              <StepCard key={stepKey}>
                {index < stepKeys.length - 1 && <StepConnector />}
                <IconContainer>
                  <IconWrapper>
                    <Icon />
                  </IconWrapper>
                  <StepNumber>{index + 1}</StepNumber>
                </IconContainer>
                <StepTitle>{t(`appLandingPage.howItWorks.steps.${index}.title`)}</StepTitle>
                <StepDescription>{t(`appLandingPage.howItWorks.steps.${index}.description`)}</StepDescription>
              </StepCard>
            );
          })}
        </StepsGrid>

        <DiveDeepSection>
          <DiveDeepTitle>{t('appLandingPage.howItWorks.diveDeep.title')}</DiveDeepTitle>
          <DiveDeepSubtitle>{t('appLandingPage.howItWorks.diveDeep.subtitle')}</DiveDeepSubtitle>

          <TwoColumnGrid style={{ marginTop: 0 }}>
          <ImageColumn order={2}>
            <img
              src={getLocalizedDelayDistributionScreenshot(i18n.language)}
              alt={t('appLandingPage.howItWorks.delaySection.imageAlt')}
              style={{ borderRadius: '2rem' }}
            />
          </ImageColumn>
          <ContentColumn order={1}>
            <SubHeading>{t('appLandingPage.howItWorks.delaySection.heading')}</SubHeading>
            <SubDescription>{t('appLandingPage.howItWorks.delaySection.description')}</SubDescription>
            <BulletList>
              {[0, 1, 2].map((index) => (
                <BulletItem key={index}>
                  <BulletDot color="violet" />
                  <BulletText>{t(`appLandingPage.howItWorks.delaySection.bullets.${index}`)}</BulletText>
                </BulletItem>
              ))}
            </BulletList>
          </ContentColumn>
        </TwoColumnGrid>

          <TwoColumnGrid>
            <ImageColumn order={1}>
              <img
                src={getLocalizedTransferEvolutionScreenshot(i18n.language)}
                alt={t('appLandingPage.howItWorks.transferSection.imageAlt')}
                style={{ borderRadius: '1.8rem' }}
              />
            </ImageColumn>
            <ContentColumn order={2}>
              <SubHeading>{t('appLandingPage.howItWorks.transferSection.heading')}</SubHeading>
              <SubDescription>{t('appLandingPage.howItWorks.transferSection.description')}</SubDescription>
              <BulletList>
                {[0, 1, 2].map((index) => (
                  <BulletItem key={index}>
                    <BulletDot />
                    <BulletText>{t(`appLandingPage.howItWorks.transferSection.bullets.${index}`)}</BulletText>
                  </BulletItem>
                ))}
              </BulletList>
            </ContentColumn>
          </TwoColumnGrid>

          <TwoColumnGrid>
            <ImageColumn order={2}>
              <img
                src={getLocalizedPlatformStatsScreenshot(i18n.language)}
                alt={t('appLandingPage.howItWorks.platformSection.imageAlt')}
                style={{ borderRadius: '4rem' }}
              />
            </ImageColumn>
            <ContentColumn order={1}>
              <SubHeading>{t('appLandingPage.howItWorks.platformSection.heading')}</SubHeading>
              <SubDescription>{t('appLandingPage.howItWorks.platformSection.description')}</SubDescription>
              <BulletList>
                {[0, 1, 2].map((index) => (
                  <BulletItem key={index}>
                    <BulletDot color="violet" />
                    <BulletText>{t(`appLandingPage.howItWorks.platformSection.bullets.${index}`)}</BulletText>
                  </BulletItem>
                ))}
              </BulletList>
            </ContentColumn>
          </TwoColumnGrid>
        </DiveDeepSection>
      </Container>
    </HowItWorksSection>
  );
}

